import {
  ICreateRechargeCustomerRequest,
  ICreateRechargeCustomerResponse,
  IPageableRechargeCustomerResponse,
  IRechargeCustomer,
  IRechargeCustomerGetParams,
  ISingleRechargeCustomerResponse,
} from "@recharge";
import { Logger } from "@nestjs/common";
import axios from "axios";

import { RechargeResourceEndpoint } from "../../RechargeEndpoint";
import { RechargeAccessTokenManager } from "../RechargeAccessTokenManager";

export class RechargeCustomersClient extends RechargeResourceEndpoint<
ISingleRechargeCustomerResponse,
IPageableRechargeCustomerResponse,
IRechargeCustomerGetParams,
IRechargeCustomer
> {

  public constructor(
    rechargeKeyManager: RechargeAccessTokenManager,
    baseUrl = "https://api.rechargeapps.com",
  ) {
    super(
      axios.create({ baseURL: baseUrl }),
      rechargeKeyManager.getNextAccessToken.bind(rechargeKeyManager),
      "/customers",
      new Logger(RechargeCustomersClient.name),
    );
  }

  protected override singleGetter(t: ISingleRechargeCustomerResponse): IRechargeCustomer {
    return t.customer;
  }

  protected override multiGetter(t: IPageableRechargeCustomerResponse): IRechargeCustomer[] {
    return t.customers;
  }

  public async createCustomer(
    /** Currently Shopify ID, to be replaced with internal ID */
    {
      externalId,
      firstName,
      lastName,
      email,
      phone,
    }: {
      externalId: string;
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
    },
  ): Promise<IRechargeCustomer> {

    const data: ICreateRechargeCustomerRequest = {
      first_name: firstName,
      last_name: lastName,
      phone,
      email,
      external_customer_id: {
        ecommerce: externalId,
      },
      tax_exempt: false,
    };

    const response = await this.requestWithRetry<ICreateRechargeCustomerResponse, ICreateRechargeCustomerRequest>({
      url: this.resourcePath,
      config: {
        headers: this.getHeaders(),
        method: "POST",
        data,
      },
      newKeyOnRetry: true,
      truncateLongBody: true,
    });

    return response.data.customer;

  }

  public async updateCustomer(
    rechargeCustomerId: number | bigint,
    {
      externalId,
      firstName,
      lastName,
      phone,
      email,
    }: {
      externalId?: string;
      firstName?: string;
      lastName?: string;
      phone?: string | null;
      email?: string;
    },
  ): Promise<IRechargeCustomer> {

    const data: Partial<ICreateRechargeCustomerRequest> = {
      first_name: firstName,
      last_name: lastName,
      phone,
      email,
      external_customer_id: externalId ? {
        ecommerce: externalId,
      } : undefined,
      tax_exempt: false,
    };

    const response = await this.requestWithRetry<
    ICreateRechargeCustomerResponse,
    Partial<ICreateRechargeCustomerRequest>
    >({
      url: `${this.resourcePath}/${rechargeCustomerId}`,
      config: {
        headers: this.getHeaders(),
        method: "PUT",
        data,
      },
      newKeyOnRetry: true,
      truncateLongBody: true,
    });

    return response.data.customer;
  }

}
